import React, {useState, useMemo, useEffect} from 'react';
import {Autocomplete, Box, IconButton, TextField} from '@mui/material';
import {useTheme} from "../../../contexts/theme";
import StyledDropdown from "../StyledDropdown/StyledDropdown";
import TransparentButton from "../TransparentButton/TransparentButton";
import {downloadVideoContent} from "../../../queries/videoContentQueries";
import {download} from "../../../utils/utils";
import {downloadEdit} from "../../../queries/editorQueries";
import {Popper} from "@mui/material";


// Lista di risoluzioni predefinite (per esempio, possono essere tutte quelle disponibili)
const allResolutions = [
    // 16:9
    { width: 1920, height: 1080 },
    { width: 1600, height: 900 },
    { width: 1280, height: 720 },
    // 9:16
    { width: 1080, height: 1920 },
    { width: 720, height: 1280 },
    { width: 480, height: 854 },
    // 4:5
    { width: 1200, height: 1500 },
    { width: 1080, height: 1350 },
    { width: 800, height: 1000 },
    { width: 640, height: 800 },
    // 1:1
    { width: 1024, height: 1024 },
    { width: 1080, height: 1080 },
    { width: 800, height: 800 },
    { width: 500, height: 500 },
];

// Funzione per filtrare le risoluzioni in base all'aspect ratio
function getResolutionsForAspectRatio(aspectRatio: any, tolerance = 0.01) {
    return allResolutions.filter(res => {
        const ratio = res.width / res.height;
        return Math.abs(ratio - aspectRatio) < tolerance;
    });
}

function calculateResolution(input: any, aspectRatio: any) {
    // Se l'input inizia per "w:" lo interpreta come larghezza, se "h:" come altezza
    let value, type;
    if (input.toLowerCase().startsWith("w:")) {
        value = parseInt(input.substring(2));
        type = 'width';
    } else if (input.toLowerCase().startsWith("h:")) {
        value = parseInt(input.substring(2));
        type = 'height';
    } else {
        // Se non viene specificato, si assume di voler inserire la larghezza
        value = parseInt(input);
        type = 'width';
    }

    if (isNaN(value)) return null;

    // Calcolo dell'altra dimensione in base al rapporto d'aspetto
    if (type === 'width') {
        return { width: value, height: Math.round(value / aspectRatio) };
    } else {
        return { width: Math.round(value * aspectRatio), height: value };
    }
}

const formatAspectRatio = (aspectRatio: string): number => {
    const width = aspectRatio.split(":")[0]
    const height = aspectRatio.split(":")[1]
    return Number(width) / Number(height)
}

export interface CustomPopperProps {
    aspectRatio: string;
    setIsClipDownloading: any;
    clips: any[];
    editSessionId?: string;
    isProductionNeeded: boolean;
    handlePopupClose: () => void;
    setDownloadButtonPopperAnchorEl: any;
}

export default function CustomPopperComponent({ handlePopupClose, aspectRatio = "16:9", setIsClipDownloading, clips, editSessionId, isProductionNeeded, setDownloadButtonPopperAnchorEl } : CustomPopperProps) {
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const theme = useTheme();
    const [colorSpaceValue, setColorSpaceValue] = useState<string>("yuv420p")
    const predefinedResolutions = useMemo(() => getResolutionsForAspectRatio(formatAspectRatio(aspectRatio)), [formatAspectRatio(aspectRatio)]);

    const CustomPopper = (props: any) => (
        <Popper
            {...props}
            style={{...props.style, zIndex: 800000}}
        />
    )

    useEffect(() => {
        setValue(null)
    }, [aspectRatio]);

    const handleChange = (event: any, newValue: any) => {
        if (typeof newValue === 'string') {
            // Se l'utente ha inserito manualmente un valore
            const res: any = calculateResolution(newValue, formatAspectRatio(aspectRatio));
            setValue(res);
        } else if (newValue && newValue.inputValue) {
            // Se viene selezionata una opzione custom creata al volo
            const res: any = calculateResolution(newValue.inputValue, formatAspectRatio(aspectRatio));
            setValue(res);
        } else {
            setValue(newValue);
        }
    };

    // Personalizziamo il filtro per includere la possibilità di inserire una risoluzione custom
    const filterOptions = (options: any, params: any) => {
        const filtered = options.filter((option: any) => {
            const optionLabel = `${option.width}x${option.height}`;
            return optionLabel.includes(params.inputValue);
        });

        // Se l'input non è vuoto, proviamo a calcolare una risoluzione custom
        if (params.inputValue !== '') {
            const res = calculateResolution(params.inputValue, formatAspectRatio(aspectRatio));
            if (res) {
                filtered.push({
                    inputValue: params.inputValue,
                    width: res.width,
                    height: res.height,
                    custom: true
                });
            }
        }

        return filtered;
    };

    const handleDownloadClipfromPopper = async () => {
        try {
            setIsClipDownloading('DOWNLOAD')

            if(isProductionNeeded && editSessionId && editSessionId !== '') {
                const response = await downloadEdit(editSessionId, aspectRatio, value, colorSpaceValue);
                handlePopupClose();
                await download(response.preview_url, clips[0].title, aspectRatio)
            } else {
                const response = await Promise.all(
                    clips.map(async (clip) => {
                        const result = await downloadVideoContent(clip.id, { resolution: value, color_space: colorSpaceValue });
                        return result.url;
                    })
                )

                await Promise.all(
                    clips.map(async (clip, index) => {
                        const title = clip.title;
                        const url = response[index];
                        await download(url, title, clip.aspect_ratio)
                    })
                )
            }

            setIsClipDownloading('NODOWNLOAD')

        } catch (error) {
            console.error("Error downloading the clip")
            setIsClipDownloading('FAILED')
        }
    }

    return (
        <Box>
            <Box sx={{ p: 1, bgcolor: theme.palette.blue, width: '100%'}}>
                <Autocomplete
                    PopperComponent={CustomPopper}
                    value={value}
                    onChange={handleChange}
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                    filterOptions={filterOptions}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    options={predefinedResolutions}
                    getOptionLabel={(option: any) => {
                        // Se l'utente inserisce direttamente il testo
                        if (typeof option === 'string') {
                            return option;
                        }
                        if (option.inputValue) {
                            return option.inputValue;
                        }
                        return `${option.width}x${option.height}`;
                    }}
                    renderOption={(props, option: any) => (
                        <li {...props}>
                            {option.custom ? `Custom: ${option.width}x${option.height}` : `${option.width}x${option.height}`}
                        </li>
                    )}
                    freeSolo
                    renderInput={(params) => (
                        <TextField sx={{
                            // Change the text color inside the input
                            input: {
                                color: theme.palette.white, // Use the color you desire
                            },
                            // Optionally change the label color
                            "& .MuiInputLabel-root": {
                                color: theme.palette.white,
                                "&.Mui-focused": {
                                    color: theme.palette.white,
                                },
                            },
                            // You can also adjust the border colors if needed
                            "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                    borderColor: theme.palette.white,
                                },
                                "&:hover fieldset": {
                                    borderColor: theme.palette.white,
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: theme.palette.white,
                                },
                            },
                        }} {...params} label="Select Resolution" variant="outlined" />
                    )}
                />
            </Box>
            <Box sx={{ p: 1, bgcolor: theme.palette.blue, width: '100%'}}>
                <StyledDropdown fullWidth={true} options={["yuv420p", "yuv422p"]} label={"Select color space"} value={colorSpaceValue} id={"color-space"} onChange={(event) => {setColorSpaceValue(event.target.value)}}/>
            </Box>
            <Box sx={{display: 'flex', justifyContent: 'right', p: 1}}>
                <TransparentButton disabled={value === null} text={'Download'} onClick={() => {
                    setDownloadButtonPopperAnchorEl(null)
                    handleDownloadClipfromPopper();
                }}/>
            </Box>
        </Box>
    );
}